<template>
  <dialog
    ref="dialog"
    :class="$style['lightbox']"
    :aria-label="label"
    @close="onClose"
    @cancel="exit.onCancel"
    @keydown.left.prevent="go(index - 1)"
    @keydown.right.prevent="go(index + 1)">
    <!-- Swipe between the photos; a click next to a photo closes, a click on it zooms in (with a mouse) -->
    <div
      ref="track"
      :class="[$style['lightbox__track'], zoomed && $style['lightbox__track--zoomed']]"
      @scroll.passive="onScroll">
      <div
        v-for="(image, i) in images"
        :key="image.src"
        :class="$style['lightbox__slide']"
        @click="onSlideClick($event, i)">
        <img
          :src="image.src"
          :alt="image.alt"
          :loading="Math.abs(i - start) <= 1 ? 'eager' : 'lazy'"
          :class="[$style['lightbox__image'], zoomed && i === index && $style['lightbox__image--zoomed']]"
          :style="zoomed && i === index ? { transformOrigin: origin } : undefined"
          @mousemove="zoomed && pan($event, ($event.currentTarget as HTMLElement).parentElement!)">
      </div>
    </div>

    <p v-if="images.length > 1" :class="$style['lightbox__count']" aria-hidden="true">{{ index + 1 }} / {{ images.length }}</p>
    <template v-if="images.length > 1">
      <Button
        icon-only
        round
        icon="ChevronLeft"
        aria-label="Vorige foto"
        :disabled="index === 0"
        :class="[$style['lightbox__step'], $style['lightbox__step--previous']]"
        @click="go(index - 1)" />
      <Button
        icon-only
        round
        icon="ChevronRight"
        aria-label="Volgende foto"
        :disabled="index === images.length - 1"
        :class="[$style['lightbox__step'], $style['lightbox__step--next']]"
        @click="go(index + 1)" />
    </template>
    <Button
      icon-only
      round
      icon="X"
      aria-label="Sluit"
      autofocus
      :class="$style['lightbox__close']"
      @click="close" />
    <p :class="$style['lightbox__sr-only']" role="status">{{ images.length > 1 ? images[index]?.alt : '' }}</p>
  </dialog>
</template>

<script setup lang="ts">
import { useCssModule } from 'vue'

// Photos over the whole screen, in a native dialog: Escape closes it and the keyboard stays inside.
// Swipe or use the arrows to go through them. With a mouse, a click on the photo zooms in on that spot.
const props = withDefaults(defineProps<{
  images: { src: string, alt: string }[]
  label: string
  start?: number
}>(), { start: 0 })

// Says which photo was open last, so whatever opened it can show the same one
const emit = defineEmits<{ close: [index: number] }>()

const dialog = ref<HTMLDialogElement>()
const track = ref<HTMLElement>()
const index = ref(props.start)
const zoomed = ref(false)
const origin = ref('50% 50%')
let returnFocus: HTMLElement | null = null
let overflow = ''
let frame = 0

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
const finePointer = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches

function go (to: number) {
  if (!track.value || to < 0 || to >= props.images.length) { return }
  zoomed.value = false
  index.value = to
  track.value.scrollTo({ left: to * track.value.clientWidth, behavior: reducedMotion() ? 'instant' : 'smooth' })
}

function onScroll () {
  cancelAnimationFrame(frame)
  frame = requestAnimationFrame(() => {
    if (!track.value) { return }
    const now = Math.round(track.value.scrollLeft / track.value.clientWidth)
    if (now !== index.value) {
      index.value = now
      zoomed.value = false
    }
  })
}

function onSlideClick (event: MouseEvent, i: number) {
  if (event.target !== event.currentTarget) {
    // On the photo: zoom in or out where you clicked
    if (!finePointer() || i !== index.value) { return }
    pan(event, event.currentTarget as HTMLElement)
    zoomed.value = !zoomed.value
    return
  }
  close()
}

// While zoomed in, the photo follows the mouse across its slide
function pan (event: MouseEvent, slide: HTMLElement) {
  const box = slide.getBoundingClientRect()
  const x = Math.min(100, Math.max(0, ((event.clientX - box.left) / box.width) * 100))
  const y = Math.min(100, Math.max(0, ((event.clientY - box.top) / box.height) * 100))
  origin.value = `${x}% ${y}%`
}

// It fades out the way it faded in
const exit = useDialogExit(dialog, useCssModule()['lightbox--closing']!)
const close = () => exit.close()

function onClose () {
  document.documentElement.style.overflow = overflow
  // Safari does not give the focus back by itself
  returnFocus?.focus({ preventScroll: true })
  emit('close', index.value)
}

onMounted(async () => {
  returnFocus = document.activeElement as HTMLElement | null
  // The page behind stays where it is
  overflow = document.documentElement.style.overflow
  document.documentElement.style.overflow = 'hidden'
  dialog.value?.showModal()
  await nextTick()
  if (track.value) { track.value.scrollLeft = props.start * track.value.clientWidth }
})

onBeforeUnmount(() => {
  cancelAnimationFrame(frame)
  document.documentElement.style.overflow = overflow
})
</script>

<style module>
/* The whole screen; the global margin reset would otherwise pin it to the top left */
.lightbox {
  position: fixed;
  inset: 0;
  width: 100%;
  max-width: none;
  height: 100%;
  max-height: none;
  margin: 0;
  padding: 0;
  overflow: hidden;
  border: 0;
  background: transparent;
  color: var(--stage-white);
}

.lightbox[open] {
  animation: lightbox-in 250ms ease-out;
}

.lightbox::backdrop {
  background: oklch(0% 0 0deg / 96%);
}

@keyframes lightbox-in {
  from {
    opacity: 0;
  }
}

.lightbox--closing[open],
.lightbox--closing[open]::backdrop {
  animation: lightbox-out 200ms ease-in forwards;
}

@keyframes lightbox-out {
  to {
    opacity: 0;
  }
}

.lightbox__track {
  display: flex;
  height: 100%;
  overflow-x: auto;
  overscroll-behavior: contain;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
}

.lightbox__track::-webkit-scrollbar {
  display: none;
}

/* Zoomed in, a stray swipe does not take you to the next photo */
.lightbox__track--zoomed {
  overflow-x: hidden;
}

.lightbox__slide {
  display: grid;
  flex: 0 0 100%;
  place-items: center;
  overflow: hidden;
  padding: var(--spacing-xxxxl) var(--spacing-l);
  scroll-snap-align: center;
  scroll-snap-stop: always;
}

.lightbox__image {
  display: block;
  max-width: 100%;
  max-height: calc(100dvh - 2 * var(--spacing-xxxxl));
  object-fit: contain;
  transition: transform 300ms var(--stage-ease);
}

@media (hover: hover) and (pointer: fine) {
  .lightbox__image {
    cursor: zoom-in;
  }

  .lightbox__image--zoomed {
    cursor: zoom-out;
    transform: scale(2.25);
  }
}

.lightbox__count {
  position: absolute;
  top: var(--spacing-xl);
  left: var(--spacing-xl);
  margin: 0;
  color: var(--stage-soft);
  font-size: var(--font-size-small);
  font-variant-numeric: tabular-nums;
  line-height: 3rem;
}

.lightbox__close {
  position: absolute;
  top: var(--spacing-l);
  right: var(--spacing-l);
}

.lightbox__step {
  position: absolute;
  top: calc(50% - 1.75rem);
}

.lightbox__step--previous {
  left: var(--spacing-l);
}

.lightbox__step--next {
  right: var(--spacing-l);
}

/* Dark behind the buttons, so they stay visible on a light photo */
.lightbox .lightbox__close,
.lightbox .lightbox__step {
  --background-color: oklch(0% 0 0deg / 60%);
}

.lightbox .lightbox__step[disabled] {
  opacity: 0;
  pointer-events: none;
}

/* On a phone you swipe; the arrows would cover the photo */
@media (hover: none) {
  .lightbox__step {
    display: none;
  }
}

.lightbox__sr-only {
  position: absolute;
  width: var(--stage-hairline);
  height: var(--stage-hairline);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

@media (prefers-reduced-motion: reduce) {
  .lightbox[open] {
    animation: none;
  }

  .lightbox__image {
    transition: none;
  }
}
</style>
